//! Draws a chart display list (produced by quadratic-core's chart layout
//! engine) into a Pixi container. The display list is a flat array of
//! primitives with resolved colors; this module contains no chart semantics.
//!
//! Text uses the MSDF OpenSans atlas (same as table names and cell text) so
//! labels stay crisp at every zoom level, rather than canvas-rasterized `Text`
//! which gets blocky when scaled up. All of a chart's labels are batched into
//! a few shared meshes (see `ChartTextBuilder`) rather than one `BitmapText`
//! per label, which would cost a draw call each. Batched text draws above the
//! graphics within its clip scope, which is correct for charts (labels are
//! topmost).
//!
//! Rich fills and effects:
//! - Linear gradients render via a generated 256x1 gradient texture mapped
//!   across the shape's bounding box at the gradient angle.
//! - Picture fills render via textures decoded from the chart's image table
//!   (`JsChart.images`). Image decode is asynchronous; shapes draw with their
//!   fallback color first and the caller is asked to redraw once textures
//!   are ready (`onNeedsRedraw`).
//! - `PushEffect`/`PopEffect` scopes render shadows and glows as a blurred
//!   silhouette of the enclosed ops behind them, and soft edges as a slight
//!   blur of the ops themselves.

import { ChartTextBuilder } from '@/app/gridGL/charts/ChartTextBuilder';
import type {
  DrawOp,
  EffectStyle,
  FillStyle,
  GradientStyle,
  JsChartImage,
  StrokeStyle,
} from '@/app/quadratic-core-types';
import { BlurFilter, Container, Graphics, Matrix, Texture, WRAP_MODES } from 'pixi.js';

/** Axis-aligned bounds of a filled shape, used to map gradient/image fills. */
interface FillBounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** When set, fills/strokes draw in this flat color (shadow/glow passes). */
interface Silhouette {
  color: number;
  alpha: number;
}

interface RenderContext {
  /** Textures for the chart's image table, keyed by image index. */
  imageTextures: (Texture | undefined)[];
  silhouette?: Silhouette;
}

// gradient textures are cached by their stop list; image textures by the
// base64 payload (many charts reuse the same image across redraws)
const gradientTextureCache = new Map<string, Texture>();
const imageTextureCache = new Map<string, Texture>();

function cssColor(color: number, alpha: number): string {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  return `rgba(${r},${g},${b},${alpha})`;
}

function gradientTexture(gradient: GradientStyle): Texture {
  const key = JSON.stringify(gradient.stops);
  const cached = gradientTextureCache.get(key);
  if (cached) return cached;
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Texture.WHITE;
  const grad = ctx.createLinearGradient(0, 0, 256, 0);
  for (const stop of gradient.stops) {
    grad.addColorStop(Math.min(1, Math.max(0, stop.position)), cssColor(stop.color, stop.alpha));
  }
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 256, 1);
  const texture = Texture.from(canvas);
  gradientTextureCache.set(key, texture);
  return texture;
}

/**
 * The texture for an embedded chart image. Decoding is asynchronous: until
 * the texture is valid the caller draws fallback colors, and `onLoaded`
 * fires when a redraw would pick the texture up.
 */
function imageTexture(image: JsChartImage, onLoaded?: () => void): Texture {
  let texture = imageTextureCache.get(image.data);
  if (!texture) {
    texture = Texture.from(`data:${image.mime};base64,${image.data}`);
    imageTextureCache.set(image.data, texture);
  }
  if (!texture.valid && onLoaded) {
    texture.baseTexture.once('loaded', onLoaded);
  }
  return texture;
}

/** Matrix mapping the 256x1 gradient texture across `bounds` at the angle. */
function gradientMatrix(gradient: GradientStyle, bounds: FillBounds): Matrix {
  const rad = (gradient.angle_deg * Math.PI) / 180;
  const dx = Math.cos(rad);
  const dy = Math.sin(rad);
  const corners = [
    { x: bounds.x, y: bounds.y },
    { x: bounds.x + bounds.w, y: bounds.y },
    { x: bounds.x, y: bounds.y + bounds.h },
    { x: bounds.x + bounds.w, y: bounds.y + bounds.h },
  ];
  const projections = corners.map((c) => c.x * dx + c.y * dy);
  const start = Math.min(...projections);
  const extent = Math.max(...projections) - start;
  const origin = corners[projections.indexOf(start)];
  const m = new Matrix();
  m.scale(Math.max(extent, 1) / 256, Math.max(bounds.w, bounds.h, 1));
  m.rotate(rad);
  m.translate(origin.x, origin.y);
  return m;
}

/** Begins a fill on `g`; returns whether `endFill` is needed. */
function applyFill(g: Graphics, fill: FillStyle | null | undefined, bounds: FillBounds, ctx: RenderContext): boolean {
  if (!fill) return false;
  if (ctx.silhouette) {
    g.beginFill(ctx.silhouette.color, ctx.silhouette.alpha * fill.alpha);
    return true;
  }
  if (fill.image) {
    const texture = ctx.imageTextures[fill.image.image];
    if (texture?.valid) {
      const m = new Matrix();
      if (fill.image.tile) {
        texture.baseTexture.wrapMode = WRAP_MODES.REPEAT;
        m.translate(bounds.x, bounds.y);
      } else {
        m.scale(bounds.w / texture.width, bounds.h / texture.height);
        m.translate(bounds.x, bounds.y);
      }
      g.beginTextureFill({ texture, matrix: m, alpha: fill.image.alpha });
      return true;
    }
    // fall through to the fallback color while the image decodes
  } else if (fill.gradient && fill.gradient.stops.length >= 2) {
    g.beginTextureFill({
      texture: gradientTexture(fill.gradient),
      matrix: gradientMatrix(fill.gradient, bounds),
      alpha: 1,
    });
    return true;
  }
  g.beginFill(fill.color, fill.alpha);
  return true;
}

function applyStroke(g: Graphics, stroke: StrokeStyle | null | undefined, ctx: RenderContext) {
  if (stroke) {
    if (ctx.silhouette) {
      g.lineStyle({ width: stroke.width, color: ctx.silhouette.color, alpha: ctx.silhouette.alpha * stroke.alpha });
    } else {
      g.lineStyle({ width: stroke.width, color: stroke.color, alpha: stroke.alpha });
    }
  } else {
    g.lineStyle();
  }
}

/** Draws a (possibly dashed) line segment. */
function drawSegment(g: Graphics, x1: number, y1: number, x2: number, y2: number, stroke: StrokeStyle) {
  if (!stroke.dash || stroke.dash.length < 2) {
    g.moveTo(x1, y1);
    g.lineTo(x2, y2);
    return;
  }
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.hypot(dx, dy);
  if (length === 0) return;
  const ux = dx / length;
  const uy = dy / length;
  let travelled = 0;
  let dashIndex = 0;
  let penDown = true;
  let x = x1;
  let y = y1;
  g.moveTo(x, y);
  while (travelled < length) {
    const dashLength = Math.min(stroke.dash[dashIndex % stroke.dash.length], length - travelled);
    x += ux * dashLength;
    y += uy * dashLength;
    if (penDown) {
      g.lineTo(x, y);
    } else {
      g.moveTo(x, y);
    }
    travelled += dashLength;
    dashIndex++;
    penDown = !penDown;
  }
}

function polygonBounds(points: number[]): FillBounds {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i + 1 < points.length; i += 2) {
    minX = Math.min(minX, points[i]);
    maxX = Math.max(maxX, points[i]);
    minY = Math.min(minY, points[i + 1]);
    maxY = Math.max(maxY, points[i + 1]);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** The index of the PopEffect matching the PushEffect at `start`. */
function matchingPopEffect(ops: DrawOp[], start: number): number {
  let depth = 0;
  for (let i = start + 1; i < ops.length; i++) {
    const type = ops[i].type;
    if (type === 'PushEffect') depth++;
    else if (type === 'PopEffect') {
      if (depth === 0) return i;
      depth--;
    }
  }
  return ops.length;
}

/** Renders ops into `parent`, handling clips, effects and batched text. */
function renderOps(parent: Container, ops: DrawOp[], ctx: RenderContext) {
  // clip stack: ops after PushClip render into a masked sub-container
  let current = parent;
  const clipStack: Container[] = [];
  let graphics = current.addChild(new Graphics());

  const newGraphics = () => {
    graphics = current.addChild(new Graphics());
  };

  // text is batched per container (so clipped text stays inside its mask) and
  // the meshes are added after all ops are drawn
  const textBuilders = new Map<Container, ChartTextBuilder>();
  const textBuilder = (): ChartTextBuilder => {
    let builder = textBuilders.get(current);
    if (!builder) {
      builder = new ChartTextBuilder();
      textBuilders.set(current, builder);
    }
    return builder;
  };

  for (let i = 0; i < ops.length; i++) {
    const op = ops[i];
    switch (op.type) {
      case 'Rect': {
        applyStroke(graphics, op.stroke, ctx);
        const filled = applyFill(graphics, op.fill, { x: op.x, y: op.y, w: op.w, h: op.h }, ctx);
        graphics.drawRect(op.x, op.y, op.w, op.h);
        if (filled) graphics.endFill();
        break;
      }
      case 'Line': {
        applyStroke(graphics, op.stroke, ctx);
        drawSegment(graphics, op.x1, op.y1, op.x2, op.y2, op.stroke);
        break;
      }
      case 'Polyline': {
        applyStroke(graphics, op.stroke, ctx);
        if (op.stroke.dash && op.stroke.dash.length >= 2) {
          for (let j = 0; j + 3 < op.points.length; j += 2) {
            drawSegment(graphics, op.points[j], op.points[j + 1], op.points[j + 2], op.points[j + 3], op.stroke);
          }
        } else if (op.points.length >= 4) {
          graphics.moveTo(op.points[0], op.points[1]);
          for (let j = 2; j + 1 < op.points.length; j += 2) {
            graphics.lineTo(op.points[j], op.points[j + 1]);
          }
        }
        break;
      }
      case 'Polygon': {
        applyStroke(graphics, op.stroke, ctx);
        const filled = applyFill(graphics, op.fill, polygonBounds(op.points), ctx);
        graphics.drawPolygon(op.points);
        if (filled) graphics.endFill();
        break;
      }
      case 'Circle': {
        applyStroke(graphics, op.stroke, ctx);
        const bounds = { x: op.cx - op.radius, y: op.cy - op.radius, w: op.radius * 2, h: op.radius * 2 };
        const filled = applyFill(graphics, op.fill, bounds, ctx);
        graphics.drawCircle(op.cx, op.cy, op.radius);
        if (filled) graphics.endFill();
        break;
      }
      case 'PieSlice': {
        // display-list angles are clockwise from 12 o'clock; canvas angles
        // are clockwise (y-down) from 3 o'clock
        const start = op.start_angle - Math.PI / 2;
        const end = op.end_angle - Math.PI / 2;
        applyStroke(graphics, op.stroke, ctx);
        const bounds = { x: op.cx - op.radius, y: op.cy - op.radius, w: op.radius * 2, h: op.radius * 2 };
        const filled = applyFill(graphics, op.fill, bounds, ctx);
        if (op.inner_radius > 0) {
          graphics.moveTo(op.cx + op.inner_radius * Math.cos(start), op.cy + op.inner_radius * Math.sin(start));
          graphics.lineTo(op.cx + op.radius * Math.cos(start), op.cy + op.radius * Math.sin(start));
          graphics.arc(op.cx, op.cy, op.radius, start, end);
          graphics.lineTo(op.cx + op.inner_radius * Math.cos(end), op.cy + op.inner_radius * Math.sin(end));
          graphics.arc(op.cx, op.cy, op.inner_radius, end, start, true);
          graphics.closePath();
        } else {
          graphics.moveTo(op.cx, op.cy);
          graphics.arc(op.cx, op.cy, op.radius, start, end);
          graphics.closePath();
        }
        if (filled) graphics.endFill();
        break;
      }
      case 'Text': {
        // shadow/glow silhouettes skip text (chart text effects are not
        // visually significant at chart scale)
        if (!ctx.silhouette) {
          textBuilder().add(op);
        }
        break;
      }
      case 'PushClip': {
        const clipped = new Container();
        const mask = new Graphics();
        mask.beginFill(0xffffff);
        mask.drawRect(op.x, op.y, op.w, op.h);
        mask.endFill();
        clipped.addChild(mask);
        clipped.mask = mask;
        current.addChild(clipped);
        clipStack.push(current);
        current = clipped;
        newGraphics();
        break;
      }
      case 'PopClip': {
        const previous = clipStack.pop();
        if (previous) {
          current = previous;
          newGraphics();
        }
        break;
      }
      case 'PushEffect': {
        const endIndex = matchingPopEffect(ops, i);
        const subOps = ops.slice(i + 1, endIndex);
        renderEffectScope(current, subOps, op.effect, ctx);
        newGraphics();
        i = endIndex;
        break;
      }
      case 'PopEffect': {
        // handled by PushEffect; a stray PopEffect is a no-op
        break;
      }
    }
  }

  for (const [container, builder] of textBuilders) {
    for (const object of builder.createDisplayObjects()) {
      container.addChild(object);
    }
  }
}

/**
 * Renders an effect scope: blurred shadow/glow silhouettes of the enclosed
 * ops behind them, then the ops themselves (optionally soft-edged).
 */
function renderEffectScope(parent: Container, subOps: DrawOp[], effect: EffectStyle, ctx: RenderContext) {
  if (ctx.silhouette) {
    // already inside a silhouette pass: effects don't nest, draw plain
    renderOps(parent, subOps, ctx);
    return;
  }
  if (effect.glow) {
    const glow = parent.addChild(new Container());
    renderOps(glow, subOps, { ...ctx, silhouette: { color: effect.glow.color, alpha: effect.glow.alpha } });
    glow.filters = [new BlurFilter(Math.max(effect.glow.radius / 2, 1))];
  }
  if (effect.shadow) {
    const shadow = parent.addChild(new Container());
    renderOps(shadow, subOps, { ...ctx, silhouette: { color: effect.shadow.color, alpha: effect.shadow.alpha } });
    shadow.position.set(effect.shadow.dx, effect.shadow.dy);
    if (effect.shadow.blur > 0) {
      shadow.filters = [new BlurFilter(Math.max(effect.shadow.blur / 2, 1))];
    }
  }
  const main = parent.addChild(new Container());
  renderOps(main, subOps, ctx);
  if (effect.soft_edge) {
    main.filters = [new BlurFilter(Math.max(effect.soft_edge / 2, 0.5))];
  }
}

/**
 * Renders the display list into a fresh set of children on `parent`.
 * The caller is responsible for positioning `parent` at the chart's top-left.
 *
 * `images` is the chart's embedded image table (picture fills reference it
 * by index). Image decode is asynchronous: when a texture finishes loading
 * after this call, `onNeedsRedraw` is invoked once so the caller can redraw.
 */
export function drawDisplayList(parent: Container, ops: DrawOp[], images?: JsChartImage[], onNeedsRedraw?: () => void) {
  parent.removeChildren().forEach((child) => child.destroy({ children: true }));

  let redrawRequested = false;
  const onLoaded = () => {
    if (!redrawRequested && !parent.destroyed) {
      redrawRequested = true;
      onNeedsRedraw?.();
    }
  };
  const imageTextures = (images ?? []).map((image) => imageTexture(image, onNeedsRedraw ? onLoaded : undefined));

  renderOps(parent, ops, { imageTextures });
}
